import React, { useState, useEffect, useCallback } from 'react';
import { X, Loader2, Cloud, Folder, ChevronLeft, Check, Image, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { listOneDriveFiles, formatFileSize } from '../services/oneDriveService';

const OneDriveBrowserModal = ({ isOpen, onClose, brand, onSelect }) => {
    const { currentUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [files, setFiles] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [folderStack, setFolderStack] = useState([{ id: 'root', name: 'OneDrive' }]);
    const [importing, setImporting] = useState(false);

    const currentFolder = folderStack[folderStack.length - 1];

    const loadFiles = useCallback(async (folderId = null) => {
        if (!currentUser?.uid || !brand?.id) return;

        setLoading(true);
        setError(null);

        try {
            const result = await listOneDriveFiles(currentUser.uid, brand.id, folderId);
            setFiles(result.items || []);
        } catch (err) {
            console.error('Error loading OneDrive files:', err);
            setError(err.message);
            setFiles([]);
        } finally {
            setLoading(false);
        }
    }, [currentUser?.uid, brand?.id]);

    // Load files when folder changes
    useEffect(() => {
        if (isOpen && currentFolder) {
            loadFiles(currentFolder.id === 'root' ? null : currentFolder.id);
        }
    }, [isOpen, currentFolder, loadFiles]);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setFiles([]);
            setSelectedFiles([]);
            setFolderStack([{ id: 'root', name: 'OneDrive' }]);
            setError(null);
        }
    }, [isOpen]);

    const handleFolderClick = (folder) => {
        setFolderStack(prev => [...prev, { id: folder.id, name: folder.name }]);
        setSelectedFiles([]);
    };

    const handleBack = () => {
        if (folderStack.length > 1) {
            setFolderStack(prev => prev.slice(0, -1));
            setSelectedFiles([]);
        }
    };

    const handleFileToggle = (file) => {
        setSelectedFiles(prev => {
            const isSelected = prev.some(f => f.id === file.id);
            if (isSelected) {
                return prev.filter(f => f.id !== file.id);
            } else {
                return [...prev, file];
            }
        });
    };

    const handleSelectAll = () => {
        const imageFiles = files.filter(f => !f.isFolder);
        if (selectedFiles.length === imageFiles.length) {
            setSelectedFiles([]);
        } else {
            setSelectedFiles(imageFiles);
        }
    };

    const handleImport = async () => {
        if (selectedFiles.length === 0) return;

        setImporting(true);
        try {
            // Pass selected files to parent for importing
            if (onSelect) {
                await onSelect(selectedFiles);
            }
            onClose(true);
        } catch (err) {
            console.error('Error importing files:', err);
            setError(err.message);
        } finally {
            setImporting(false);
        }
    };

    if (!isOpen) return null;

    const imageFiles = files.filter(f => !f.isFolder);
    const allSelected = imageFiles.length > 0 && selectedFiles.length === imageFiles.length;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                backdropFilter: 'blur(4px)'
            }}
            onClick={() => !importing && onClose()}
        >
            <div
                className="card"
                style={{
                    maxWidth: '900px',
                    width: '95%',
                    maxHeight: '85vh',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--space-4)',
                    paddingBottom: 'var(--space-4)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: 'var(--radius-md)',
                            background: 'linear-gradient(135deg, #0078D4 0%, #005A9E 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Cloud size={20} color="white" />
                        </div>
                        <div>
                            <h2 style={{ margin: 0 }}>Browse OneDrive</h2>
                            <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                                Select images to link to your library
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="btn btn-ghost"
                        style={{ padding: 'var(--space-2)' }}
                        disabled={importing}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Breadcrumb / Navigation */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    marginBottom: 'var(--space-4)'
                }}>
                    {folderStack.length > 1 && (
                        <button
                            className="btn btn-ghost"
                            onClick={handleBack}
                            style={{ padding: 'var(--space-2)' }}
                        >
                            <ChevronLeft size={20} />
                        </button>
                    )}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-secondary)'
                    }}>
                        {folderStack.map((folder, index) => (
                            <React.Fragment key={folder.id}>
                                {index > 0 && <span>/</span>}
                                <span style={{
                                    color: index === folderStack.length - 1 ? 'var(--color-text-primary)' : 'inherit',
                                    fontWeight: index === folderStack.length - 1 ? 500 : 400
                                }}>
                                    {folder.name}
                                </span>
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            background: 'var(--color-error-bg)',
                            color: 'var(--color-error)',
                            padding: 'var(--space-3)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)',
                            border: '1px solid var(--color-error)',
                            fontSize: 'var(--font-size-sm)'
                        }}
                    >
                        <AlertCircle size={16} />
                        {error}
                    </div>
                )}

                {/* File Grid */}
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    minHeight: '300px'
                }}>
                    {loading ? (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '200px',
                            color: 'var(--color-text-secondary)'
                        }}>
                            <Loader2 size={32} className="spinning" />
                        </div>
                    ) : files.length === 0 ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '200px',
                            color: 'var(--color-text-secondary)'
                        }}>
                            <Cloud size={48} style={{ marginBottom: 'var(--space-3)', opacity: 0.5 }} />
                            <p style={{ margin: 0 }}>No images found in this folder</p>
                        </div>
                    ) : (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                            gap: 'var(--space-3)'
                        }}>
                            {files.map(file => (
                                <div
                                    key={file.id}
                                    onClick={() => file.isFolder ? handleFolderClick(file) : handleFileToggle(file)}
                                    style={{
                                        border: `2px solid ${selectedFiles.some(f => f.id === file.id) ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                        borderRadius: 'var(--radius-md)',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        background: selectedFiles.some(f => f.id === file.id) ? 'var(--color-primary-bg)' : 'var(--color-bg-secondary)',
                                        transition: 'all 0.15s ease'
                                    }}
                                >
                                    {/* Thumbnail */}
                                    <div style={{
                                        aspectRatio: '1',
                                        background: 'var(--color-bg-tertiary)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}>
                                        {file.isFolder ? (
                                            <Folder size={40} color="var(--color-primary)" />
                                        ) : file.thumbnail ? (
                                            <img
                                                src={file.thumbnail}
                                                alt={file.name}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        ) : (
                                            <Image size={40} color="var(--color-text-secondary)" />
                                        )}

                                        {/* Selection indicator */}
                                        {!file.isFolder && selectedFiles.some(f => f.id === file.id) && (
                                            <div style={{
                                                position: 'absolute',
                                                top: 'var(--space-2)',
                                                right: 'var(--space-2)',
                                                width: '24px',
                                                height: '24px',
                                                borderRadius: '50%',
                                                background: 'var(--color-primary)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <Check size={14} color="white" />
                                            </div>
                                        )}
                                    </div>

                                    {/* File info */}
                                    <div style={{
                                        padding: 'var(--space-2)',
                                        borderTop: '1px solid var(--color-border)'
                                    }}>
                                        <p style={{
                                            margin: 0,
                                            fontSize: 'var(--font-size-xs)',
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {file.name}
                                        </p>
                                        {!file.isFolder && file.size && (
                                            <p className="text-secondary" style={{
                                                margin: 0,
                                                fontSize: 'var(--font-size-xs)'
                                            }}>
                                                {formatFileSize(file.size)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: 'var(--space-4)',
                    marginTop: 'var(--space-4)',
                    borderTop: '1px solid var(--color-border)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        {imageFiles.length > 0 && (
                            <button
                                className="btn btn-ghost"
                                onClick={handleSelectAll}
                                style={{ fontSize: 'var(--font-size-sm)' }}
                            >
                                {allSelected ? 'Deselect All' : 'Select All'}
                            </button>
                        )}
                        {selectedFiles.length > 0 && (
                            <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                                {selectedFiles.length} selected
                            </span>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <button
                            className="btn btn-secondary"
                            onClick={onClose}
                            disabled={importing}
                        >
                            Cancel
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={handleImport}
                            disabled={selectedFiles.length === 0 || importing}
                            style={{
                                background: 'linear-gradient(135deg, #0078D4 0%, #005A9E 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)'
                            }}
                        >
                            {importing ? (
                                <>
                                    <Loader2 size={16} className="spinning" />
                                    Linking...
                                </>
                            ) : (
                                <>
                                    <Cloud size={16} />
                                    Link {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OneDriveBrowserModal;
